import React from 'react';
import styles from './SummaryDisplay3.module.css';

export const SummaryDisplay3 = ({ sessionData, analysisHistory, currentAnalysisIndex, onNavigatePrev, onNavigateNext }) => {
  if (!sessionData) return null;

  // Helper function to preprocess and parse the analysis text
  const preprocessSection = (sectionString) => {
    // Handle both escaped and actual newlines
    sectionString = sectionString.replace(/\\n/g, '\n');
    
    // Split by lines and process
    const lines = sectionString.split('\n');
    const processedLines = [];
    
    for (let line of lines) {
      const trimmedLine = line.trim();
      
      // Skip empty lines
      if (trimmedLine === '') continue;
      
      // Check indentation level (count leading spaces)
      const leadingSpaces = line.search(/\S/);
      const isIndented = leadingSpaces >= 2;
      const isDoubleIndented = leadingSpaces >= 6;
      
      // Remove asterisks used for bold in markdown
      const hasBoldMarkers = trimmedLine.includes('**');
      let processedLine = trimmedLine.replace(/\*\*/g, '');
      
      // Main title (# header)
      if (processedLine.startsWith('# ')) {
        processedLines.push({ type: 'main-title', content: processedLine.substring(2) });
      }
      // Section headers (## headers)
      else if (processedLine.startsWith('## ')) {
        processedLines.push({ type: 'section-header', content: processedLine.substring(3) });
      }
      // Subsection headers (### headers)
      else if (processedLine.startsWith('### ')) {
        processedLines.push({ type: 'subsection-header', content: processedLine.substring(4) });
      }
      // Subsubsection headers (#### headers)
      else if (processedLine.startsWith('#### ')) {
        processedLines.push({ type: 'subsubsection-header', content: processedLine.substring(5) });
      }
      // Numbered lists with bold header (like "1. **Observed Market Signals**")
      else if (/^\d+\.\s\*\*/.test(trimmedLine)) {
        const match = trimmedLine.match(/^(\d+)\.\s\*\*(.+?)\*\*/);
        if (match) {
          const number = match[1];
          const content = match[2];
          processedLines.push({ type: 'numbered-header', number: number, content: content });
        }
      }
      // Numbered lists with colon
      else if (/^\d+\.\s/.test(processedLine) && processedLine.includes(':')) {
        const match = processedLine.match(/^(\d+)\.\s(.+)$/);
        if (match) {
          const number = match[1];
          const content = match[2];
          if (content.trim().endsWith(':')) {
            processedLines.push({ type: 'numbered-header', number: number, content: content.trim() });
          } else {
            const colonIndex = content.indexOf(':');
            const header = content.substring(0, colonIndex + 1).trim();
            const text = content.substring(colonIndex + 1).trim();
            processedLines.push({ type: 'numbered-header', number: number, content: header });
            if (text) {
              processedLines.push({ type: 'sub-bullet', content: text, bold: false });
            }
          }
        }
      }
      // Regular numbered lists
      else if (/^\d+\.\s/.test(processedLine)) {
        const match = processedLine.match(/^(\d+)\.\s(.+)$/);
        if (match) {
          const number = match[1];
          const content = match[2];
          processedLines.push({ type: 'numbered-bullet', number: number, content: content, bold: false });
        }
      }
      // Indented bullets - use more sophisticated detection
      else if (isIndented && processedLine.startsWith('- ')) {
        // Very deep indentation (6+ spaces) = sub-sub-bullet
        if (isDoubleIndented) {
          processedLines.push({ type: 'sub-sub-bullet', content: processedLine.substring(2), bold: false });
        } 
        // Medium indentation (2-5 spaces) = sub-bullet
        else {
          processedLines.push({ type: 'sub-bullet', content: processedLine.substring(2), bold: false });
        }
      }
      // Bullet points with colon (only treat as header if ends with colon)
      else if (processedLine.startsWith('- ') && processedLine.includes(':') && processedLine.trim().endsWith(':')) {
        const content = processedLine.substring(2);
        processedLines.push({ type: 'bullet-header', content: content.trim(), bold: false });
      }
      // Regular bullet points (non-indented)
      else if (processedLine.startsWith('- ')) {
        processedLines.push({ type: 'bullet', content: processedLine.substring(2), bold: false });
      }
      // Standalone bold headers with colon
      else if (hasBoldMarkers && processedLine.includes(':') && processedLine.trim().endsWith(':')) {
        processedLines.push({ type: 'standalone-header', content: processedLine, bold: true });
      }
      // Horizontal rule
      else if (processedLine === '---') {
        processedLines.push({ type: 'divider', content: '' });
      }
      // Regular text
      else {
        processedLines.push({ type: 'text', content: processedLine, bold: false });
      }
    }
    
    return processedLines;
  };

  // Use fixed margins - no dynamic adjustment for stability
  const getFixedMargins = () => {
    return {
      bullet: styles.bullet,
      subBullet: styles.subBullet,
      subSubBullet: styles.subSubBullet
    };
  };

  // Helper function to render inline formatting (bold, italic, etc.)
  const renderInlineFormatting = (text) => {
    const parts = [];
    let currentIndex = 0;
    let key = 0;
    
    const regex = /(\*\*\*[^*]+\*\*\*|\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      if (match.index > currentIndex) {
        parts.push(text.substring(currentIndex, match.index));
      }
      
      const matched = match[0];
      if (matched.startsWith('***') && matched.endsWith('***')) {
        parts.push(
          <strong key={key++}><em>{matched.substring(3, matched.length - 3)}</em></strong>
        );
      } else if (matched.startsWith('**') && matched.endsWith('**')) {
        parts.push(<strong key={key++}>{matched.substring(2, matched.length - 2)}</strong>);
      } else if (matched.startsWith('*') && matched.endsWith('*')) {
        parts.push(<em key={key++}>{matched.substring(1, matched.length - 1)}</em>);
      }
      
      currentIndex = match.index + matched.length;
    }
    
    if (currentIndex < text.length) {
      parts.push(text.substring(currentIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };

  // Helper to render different line types
  const renderLine = (line, lineIndex, fixedMargins) => {
    const lineRenderers = {
      'main-title': () => (
        <h2 key={lineIndex} style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '20px', color: '#1a1a1a' }}>
          {line.content}
        </h2>
      ),
      'section-header': () => (
        <h3 key={lineIndex} style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '20px', marginBottom: '10px', color: '#2c2c2c', borderBottom: '2px solid #007bff', paddingBottom: '5px' }}>
          {line.content}
        </h3>
      ),
      'subsection-header': () => (
        <h4 key={lineIndex} style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '15px', marginBottom: '8px', color: '#444' }}>
          {line.content}
        </h4>
      ),
      'subsubsection-header': () => (
        <h5 key={lineIndex} style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '12px', marginBottom: '6px', color: '#555' }}>
          {line.content}
        </h5>
      ),
      'numbered-header': () => (
        <div key={lineIndex} className={styles.numberedHeader}>
          <span style={{ marginRight: '8px', color: '#007bff' }}>{line.number}.</span>
          <span style={{ flex: 1 }}>{renderInlineFormatting(line.content)}</span>
        </div>
      ),
      'numbered-bullet': () => (
        <div key={lineIndex} className={styles.numberedItem}>
          <span style={{ marginRight: '8px', color: '#007bff' }}>{line.number}.</span>
          <span style={{ flex: 1 }}>{renderInlineFormatting(line.content)}</span>
        </div>
      ),
      'standalone-header': () => (
        <div key={lineIndex} style={{ marginLeft: '0px', marginTop: '15px', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
          {renderInlineFormatting(line.content)}
        </div>
      ),
      'bullet-header': () => (
        <div key={lineIndex} className={styles.numberedHeader}>
          <span style={{ marginRight: '10px', color: '#007bff' }}>•</span>
          <span style={{ flex: 1 }}>{renderInlineFormatting(line.content)}</span>
        </div>
      ),
      'sub-bullet': () => (
        <div key={lineIndex} className={fixedMargins.subBullet}>
          <span style={{ marginRight: '10px', color: '#007bff', fontSize: '16px' }}>
            •
          </span>
          <span style={{ flex: 1 }}>{renderInlineFormatting(line.content)}</span>
        </div>
      ),
      'sub-sub-bullet': () => (
        <div key={lineIndex} className={fixedMargins.subSubBullet}>
          <span style={{ marginRight: '8px', color: '#ccc', fontSize: '6px', marginTop: '7px' }}>●</span>
          <span style={{ flex: 1 }}>{renderInlineFormatting(line.content)}</span>
        </div>
      ),
      'bullet': () => (
        <div key={lineIndex} className={fixedMargins.bullet}>
          <span style={{ marginRight: '10px', color: '#007bff' }}>•</span>
          <span style={{ flex: 1, lineHeight: '1.7', color: '#444' }}>{renderInlineFormatting(line.content)}</span>
        </div>
      ),
      'divider': () => (
        <hr key={lineIndex} style={{ border: 'none', borderTop: '1px solid #ddd', margin: '20px 0' }} />
      ),
      'text': () => (
        <p key={lineIndex} className={styles.textContent}>
          {renderInlineFormatting(line.content)}
        </p>
      )
    };

    return lineRenderers[line.type] ? lineRenderers[line.type]() : null;
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>AI Chart Analysis</h3>
      
      {/* Navigation Controls */}
      {analysisHistory.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={onNavigatePrev}
              disabled={currentAnalysisIndex === 0}
              style={{
                padding: '5px 10px',
                backgroundColor: currentAnalysisIndex === 0 ? '#e9ecef' : '#007bff',
                color: currentAnalysisIndex === 0 ? '#6c757d' : 'white',
                border: 'none',
                borderRadius: '3px',
                cursor: currentAnalysisIndex === 0 ? 'not-allowed' : 'pointer',
                fontSize: '12px'
              }}
            >
              ← Prev
            </button>
            <span style={{ fontSize: '12px', color: '#6c757d' }}>
              {currentAnalysisIndex + 1} of {analysisHistory.length}
            </span>
            <button
              onClick={onNavigateNext}
              disabled={currentAnalysisIndex === analysisHistory.length - 1}
              style={{
                padding: '5px 10px',
                backgroundColor: currentAnalysisIndex === analysisHistory.length - 1 ? '#e9ecef' : '#007bff',
                color: currentAnalysisIndex === analysisHistory.length - 1 ? '#6c757d' : 'white',
                border: 'none',
                borderRadius: '3px',
                cursor: currentAnalysisIndex === analysisHistory.length - 1 ? 'not-allowed' : 'pointer',
                fontSize: '12px'
              }}
            >
              Next →
            </button>
          </div>
        </div>
      )}
      
      {/* Timestamp */}
      {analysisHistory.length > 0 && (
        <div style={{ 
          marginBottom: '15px', 
          fontSize: '13px', 
          color: '#6c757d', 
          fontStyle: 'italic',
          textAlign: 'center'
        }}>
          Analyzed on {new Date(analysisHistory[currentAnalysisIndex].timestamp).toLocaleString()}
        </div>
      )}
      
      {/* Analysis Content */}
      {sessionData.analysis && (() => {
        const processedLines = preprocessSection(sessionData.analysis);
        const fixedMargins = getFixedMargins();
        
        return (
          <div className={styles.analysisContent}>
            {processedLines.map((line, lineIndex) => renderLine(line, lineIndex, fixedMargins))}
          </div>
        );
      })()}

      {sessionData.originalChart && (
        <div className={styles.chartContainer}>
          <h4 className={styles.chartTitle}>Analyzed Chart:</h4>
          <img 
            src={sessionData.originalChart} 
            alt="Analyzed Chart"
            className={styles.chartImage}
          />
        </div>
      )}
    </div>
  );
};